// datemath.ts
import { parseISO, add, sub, startOfYear, startOfMonth, startOfDay, startOfHour, startOfMinute, startOfSecond } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import type { Duration } from 'date-fns';

export interface ParseOptions {
  date?: Date | number;
  roundUp?: boolean;
  timezone?: string; // IANA name, e.g. "America/New_York"
  useTimezoneForRounding?: boolean;
  separator?: string;
}

interface EvaluateOptions extends ParseOptions {
  date: Date;
}

const INTERVAL_MS_UNITS: Record<string, number> = {
  w: 7 * 24 * 60 * 60 * 1000,
  d: 24 * 60 * 60 * 1000,
  h: 60 * 60 * 1000,
  m: 60 * 1000,
  s: 1000,
  ms: 1,
} as const;

export function parseInterval(expr: string | null | undefined): number {
  if (expr == null) return 0;
  const input = expr.trim();
  if (!input) return 0;

  const tokenRe = /\s*(?<sign>[+-])?(?:(?<num>\d+(?:\.\d+)?|\.\d+))?(?<unit>ms|[A-Za-z])\s*/gy;
  let totalMs = 0;
  let lastIndex = 0;

  while (tokenRe.lastIndex < input.length) {
    const m = tokenRe.exec(input);
    if (!m) {
      const badAt = tokenRe.lastIndex;
      throw new Error(`Unexpected token '${input[badAt] ?? ""}' at index ${badAt} in interval '${input}'`);
    }

    lastIndex = tokenRe.lastIndex;
    const g = m.groups ?? {};
    const unit = g.unit;
    if (!unit) {
      throw new Error(`Missing interval unit near index ${m.index} in '${input}'`);
    }

    const unitMs = INTERVAL_MS_UNITS[unit];
    if (unitMs == null) {
      throw new Error(
        `Unknown interval unit '${unit}'. Use one of: ms, s, m, h, d, w`
      );
    }

    const sign = g.sign === '-' ? -1 : 1;
    const val = g.num == null || g.num === '' ? 1 : Number(g.num);
    if (!Number.isFinite(val)) {
      throw new Error(`Invalid interval number '${g.num}' in '${input}'`);
    }

    totalMs += sign * val * unitMs;
  }

  if (lastIndex !== input.length) {
    const tail = input.slice(lastIndex);
    const badAt = lastIndex + (tail.length - tail.trimStart().length);
    throw new Error(`Unexpected token '${input[badAt] ?? ""}' at index ${badAt} in interval '${input}'`);
  }

  return totalMs;
}

export const nowWithin = (fromExpr: string, toExpr: string) => {
  const now = new Date();
  const from = parse(fromExpr, { date: now, roundUp: false });
  const to = parse(toExpr, { date: now, roundUp: true });
  return now >= from && now <= to;
};

export const nowWithinAndShortRange = (fromExpr: string, toExpr: string, maxRangeMs: number) => {
  const now = new Date();
  const from = parse(fromExpr, { date: now, roundUp: false });
  const to = parse(toExpr, { date: now, roundUp: true });
  return now >= from && now <= to && (to.getTime() - from.getTime()) <= maxRangeMs;
}

export default function parse(expr: string | Date | number | null, { date, roundUp=false, timezone, useTimezoneForRounding=false, separator='||' }: ParseOptions={}): Date {
  if (expr instanceof Date) return expr;
  if (typeof expr === 'number') return new Date(expr);
  if (expr == null) return new Date(date ?? Date.now());

  let math = '';
  let base: Date;

  expr = (expr || '').trim();
  if (expr.startsWith('now')) {
    base = new Date(date ?? Date.now());
    math = expr.slice(3);
    const sepIdx = math.indexOf(separator || '||');
    math = sepIdx === -1 ? math : math.slice(sepIdx + 2);
  } else {
    const sepIdx = expr.indexOf(separator || '||');
    const timeStr = sepIdx === -1 ? expr : expr.slice(0, sepIdx);
    base = parseTime(timeStr, timezone);
    math = sepIdx === -1 ? '' : expr.slice(sepIdx + 2);
  }

  if (!math) return base;
  return evaluate(math, { date: base, roundUp, timezone, useTimezoneForRounding });
}

// function evaluate(expr: string, { date, roundUp=false, timezone, useTimezoneForRounding }: EvaluateOptions): Date {
//   let i = 0;
//   let val = 0;
//   let d = date;

//   while (i < expr.length) {
//     const ch = expr[i++];
//     if (ch === ' ') {
//       // skip
//     } else if (ch === '+' || ch === '-' || ch === 'P' || /[0-9]/.test(ch)) {
//       // TODO: support decimal values (e.g. 1.5h)
//       const sign = ch === '-' ? -1 : 1;
//       val = /[0-9]/.test(ch) ? Number(ch) : 1;
//       while (/[0-9]/.test(expr[i])) val = val * 10 + Number(expr[i++]);
//       if (expr[i] === '.') {
//         i++;
//         let decimalPlace = 0.1;
//         while (/[0-9]/.test(expr[i])) {
//           val += Number(expr[i++]) * decimalPlace;
//           decimalPlace *= 0.1;
//         }
//       }
//       val = val || 1;
//       const unit = expr[i++];
//       d = shift(d, sign * val, unit);
//     } else if (ch === '/') {
//       // TODO: support rounding by N units (e.g. now/5m)
//       const unit = expr[i++];
//       d = round(d, unit, roundUp, timezone, useTimezoneForRounding);
//     } else {
//       throw new Error(`Unexpected token '${ch}' in date math '${expr}'`);
//     }
//   }

//   return d;
// }

export function evaluate(
  expr: string,
  { date, roundUp = false, timezone, useTimezoneForRounding }: EvaluateOptions
): Date {
  /**
   * Tokens:
   *  Shift: [+|-]?[P]?<number?> <unit>
   *          +1h  -2d  P3w  +.5h  +m
   *
   *  Round: /<number?> <unit>
   *          /h  /5h  /.5d
   */
  const tokenRe =
    /\s*(?:\/(?:(?<roundNum>\d+(?:\.\d+)?|\.\d+))?(?<roundUnit>[A-Za-z])|(?<sign>[+-])?(?:P)?(?:(?<num>\d+(?:\.\d+)?|\.\d+))?(?<shiftUnit>[A-Za-z]))\s*/gy;

  let d = date;
  let lastIndex = 0;

  while (tokenRe.lastIndex < expr.length) {
    const m = tokenRe.exec(expr);
    if (!m) {
      const badAt = tokenRe.lastIndex;
      throw new Error(`Unexpected token '${expr[badAt] ?? ""}' at index ${badAt} in date math '${expr}'`);
    }

    lastIndex = tokenRe.lastIndex;
    const g = m.groups ?? {};

    // ---- ROUND TOKEN ----
    if (g.roundUnit) {
      const magnitude = g.roundNum == null || g.roundNum === "" ? 1 : Number(g.roundNum);

      if (!Number.isFinite(magnitude)) {
        throw new Error(`Invalid rounding magnitude '${g.roundNum}' in '${expr}'`);
      }

      d = round(
        d, magnitude, g.roundUnit, roundUp,
        timezone, useTimezoneForRounding
      );

      continue;
    }

    // ---- SHIFT TOKEN ----
    const unit = g.shiftUnit;
    if (!unit) {
      throw new Error(`Missing unit in date math '${expr}' near index ${m.index}`);
    }

    const sign = g.sign === "-" ? -1 : 1;
    const val = g.num == null || g.num === "" ? 1 : Number(g.num);

    if (!Number.isFinite(val)) {
      throw new Error(`Invalid number '${g.num}' in date math '${expr}'`);
    }

    d = shift(d, sign * val, unit);
  }

  // catch trailing junk
  if (lastIndex !== expr.length) {
    const tail = expr.slice(lastIndex);
    const badAt = lastIndex + (tail.length - tail.trimStart().length);
    throw new Error(`Unexpected token '${expr[badAt] ?? ""}' at index ${badAt} in date math '${expr}'`);
  }

  return d;
}

function shift(date: Date, amount: number, unit: string): Date {
  switch (unit) {
    case 'y': return add(date, { years: amount });
    case 'M': return add(date, { months: amount });
    case 'd': return add(date, { days: amount });
    case 'w': return add(date, { weeks: amount });
    case 'h': return add(date, { hours: amount });
    case 'm': return add(date, { minutes: amount });
    case 's': return add(date, { seconds: amount });
    default: throw new Error(`Unknown unit '${unit}'`);
  }
}

function round(date: Date, magnitude: number, unit: string, roundUp: boolean, timezone?: string, useTZ?: boolean): Date {
  date = useTZ && timezone ? toZonedTime(date, timezone) : date;

  switch (unit) {
    case 'y': date = startOfYear(date); break;
    case 'M': date = startOfMonth(date); break;
    case 'd': date = startOfDay(date); break;
    case 'h': date = startOfHour(date); break;
    case 'm': date = startOfMinute(date); break;
    case 's': date = startOfSecond(date); break;
    default: throw new Error(`Unknown rounding unit '${unit}'`);
  }

  if (roundUp) date = sub(add(date, { [unitName(unit)]: magnitude }), { seconds: 1 / 1000 });
  return useTZ && timezone ? fromZonedTime(date, timezone) : date;
}

const UNIT_NAMES: Record<string, keyof Duration> = { y: "years", M: "months", d: "days", h: "hours", m: "minutes", s: "seconds" } as const;
function unitName(u: keyof typeof UNIT_NAMES) {
  return UNIT_NAMES[u];
}

function parseTime(s: string, timezone?: string): Date {
  if (/^\d+$/.test(s)) return new Date(Number(s.length === 10 ? s + "000" : s));
  const parsed = parseISO(s);
  if (timezone && !/Z|[+-]\d\d:?\d\d$/.test(s))
    return fromZonedTime(parsed, timezone);
  return parsed;
}
